import { NavLink } from "react-router-dom";
import { Settings, Shield, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/user";
import { useApi } from "../context/ApiContext";

const TABS = [
  { to: "/account/profile", label: "Profile", icon: User },
  { to: "/account/security", label: "Security", icon: Shield },
  { to: "/account/preferences", label: "Preferences", icon: Settings },
];

/** Shared frame for Profile / Security / Preferences: identity header + tabs. */
export default function AccountLayout({ children }: { children: React.ReactNode }) {
  const api = useApi([]);
  const user = api.user;
  const name = user?.name?.trim();

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">
      <div className="flex flex-wrap items-center gap-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--bg-card-elevated)] text-lg font-semibold text-[var(--text-primary)]">
          {initials(user)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="m-0 truncate text-xl font-semibold text-[var(--text-primary)]">{name || user?.email || "Account"}</h1>
            {user?.role === "admin" && (
              <span className="pill bg-[var(--bg-card-elevated)] text-[var(--text-secondary)]">Admin</span>
            )}
          </div>
          {name && user?.email && (
            <p className="m-0 mt-0.5 truncate text-sm text-[var(--text-secondary)]">{user.email}</p>
          )}
        </div>
      </div>

      <nav aria-label="Account" className="flex gap-1 overflow-x-auto border-b border-[var(--border)]">
        {TABS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cn(
                "-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm no-underline transition-colors",
                isActive
                  ? "border-[var(--primary)] font-medium text-[var(--text-primary)]"
                  : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              )
            }
          >
            <Icon size={15} />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="flex flex-col gap-4">{children}</div>
    </div>
  );
}

/** One settings block: title + short explanation on the left, controls on the right (stacked on mobile). */
export function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="grid gap-4 rounded-card border border-[var(--border)] bg-[var(--bg-card)] p-5 md:grid-cols-[minmax(0,15rem)_minmax(0,1fr)] md:gap-8">
      <div>
        <h2 className="m-0 text-[15px] font-semibold text-[var(--text-primary)]">{title}</h2>
        {description && <p className="m-0 mt-1 text-sm text-[var(--text-secondary)]">{description}</p>}
      </div>
      <div className="min-w-0">{children}</div>
    </section>
  );
}

/** Label + hint on the left, control on the right; used for toggles and segmented choices. */
export function SettingsRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t border-[var(--divider)] py-3 first:border-t-0 first:pt-0 last:pb-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-[var(--text-primary)]">{label}</div>
        {hint && <div className="mt-0.5 text-sm text-[var(--text-secondary)]">{hint}</div>}
      </div>
      {children}
    </div>
  );
}

export function FormMessage({ kind, children }: { kind: "error" | "success"; children: React.ReactNode }) {
  return (
    <div
      role={kind === "error" ? "alert" : "status"}
      className={`mb-3 rounded-[10px] px-3 py-2 text-sm ${
        kind === "error" ? "bg-[var(--danger-bg)] text-[var(--danger)]" : "bg-[var(--success-bg)] text-[var(--success)]"
      }`}
    >
      {children}
    </div>
  );
}
